import {
  ContentSetup,
  Difficulty,
  ModuleInfo,
  Progression,
  ScoredOption
} from '../../utils';
import { State } from '../state';
import { getContentType } from '../../utils/utils';
import { PageType } from 'its-pages';
import { createSelector } from 'reselect';

export const problemSetupFingerprint = (
  moduleInfo: ModuleInfo,
  difficulty: Difficulty,
  progressionIndex: number
): string => {
  const name = moduleInfo.skill.name;
  const { main, secondary } = difficulty;

  const ret = `${name}/${getContentType(
    moduleInfo,
    progressionIndex
  )}/${main}/${secondary}`;

  return ret;
};

export const problemSetupToString = (setup: ContentSetup): string => {
  const { moduleInfo, difficulty, progressionIndex } = setup;

  return problemSetupFingerprint(moduleInfo, difficulty, progressionIndex);
};

const getProblemsDoneBySetup = (
  state: State,
  problemSetup: ContentSetup
): ScoredOption[] => {
  if (!problemSetup || !problemSetup.moduleInfo) {
    return [];
  } else {
    const { moduleInfo, difficulty, progressionIndex } = problemSetup;
    const key = problemSetupFingerprint(
      moduleInfo,
      difficulty,
      progressionIndex
    );
    return state.progression.problemsDone.get(key) || [];
  }
};

export const getOptionsToIgnore = (
  state: State,
  setup: ContentSetup
): string[] => {
  const problemsDone = getProblemsDoneBySetup(state, setup);

  const ret = [];

  for (
    let i = Math.max(problemsDone.length - 3, 0);
    i < problemsDone.length;
    i++
  ) {
    ret.push(...problemsDone[i].validOptions);
  }
  return ret;
};

export const getCurrentProblemSetup = (state: State) =>
  state.currentProblemState.setup

function getProgressDenominator(progression: Progression): number {
  let denom = 0;

  progression.forEach((v1) => {
    if (v1.pageType === PageType.Problem) {
      v1.subs.forEach((v2) => {
        denom += v2 + 1;
      });
    }
  });

  return denom;
}

function getProgressNumerator(setup: ContentSetup): number {
  const {
    moduleInfo: { progression },
    progressionIndex,
    difficulty
  } = setup;
  // How is this -1
  let num = -1;

  progression.forEach((v1, i1) => {
    if (v1.pageType !== PageType.Problem) {
      return;
    }
    if (i1 < progressionIndex) {
      v1.subs.forEach((v2) => {
        num += v2 + 1;
      });
      return;
    }
    if (i1 === progressionIndex) {
      v1.subs.forEach((v2, i2) => {
        // What is `difficulty`??
        if (i2 < difficulty.main) {
          num += v2 + 1;
        } else if (i2 === difficulty.main) {
          num += difficulty.secondary + 1;
        }
      });
    }
  });

  return num;
}

/**
 * @return
 *    A value between 0-100 representing user progress towards completion
 *    of a module
 */
export const getCurrentProblemProgress = createSelector(
  getCurrentProblemSetup,
  (setup) => {
    const denominator = setup
      ? getProgressDenominator(setup.moduleInfo.progression)
      : 1;
    const numerator = setup ? getProgressNumerator(setup) : 0;
    return (numerator / denominator) * 100;
  }
);

export const getCurrentPageContent = (state: State) =>
  state.currentProblemState.pageContent;
